import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, PackageCheck, AlertTriangle, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";

const eur = (n) => (Number(n) || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });

function Concepto({ it, testId }) {
  return (
    <li className="flex items-start gap-2 text-xs py-1.5" data-testid={testId}>
      <span className="flex-1 text-zinc-300 leading-snug">{it.descripcion}</span>
      <span className="font-mono text-zinc-400 shrink-0">{it.cantidad} × {eur(it.precio_unitario)}</span>
      <span className="font-mono font-semibold text-zinc-100 w-24 text-right shrink-0">{eur(it.subtotal ?? it.cantidad * it.precio_unitario)}</span>
    </li>
  );
}

/** Cierre administrativo de una línea sin montaje → albarán/FD (sin ruta ni reporte). */
export default function FinalizarSinMontajeDialog({ linea, open, onOpenChange, onDone }) {
  const [pv, setPv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [f, setF] = useState({ motivo: "", incluir_montaje: false, justificacion_montaje: "", observacion: "", nuevo: false, transportista: "", tracking: "", fecha_salida: "" });
  const [res, setRes] = useState(null);

  useEffect(() => {
    if (!open || !linea?.id) return;
    setPv(null); setRes(null); setLoading(true);
    setF((p) => ({ ...p, motivo: "", incluir_montaje: false, justificacion_montaje: "", observacion: "", transportista: linea.transportista || "", tracking: linea.tracking_envio || "", fecha_salida: linea.fecha_envio || "" }));
    api.get(`/lineas/${linea.id}/cierre-sin-montaje/preview`).then(({ data }) => {
      setPv(data);
      setF((p) => ({ ...p, motivo: data.es_envio ? "envio_cliente" : (data.linea?.estado === "cancelada" ? "montaje_cancelado_tras_produccion" : "") }));
    }).catch((e) => toast.error(e.response?.data?.detail?.msg || e.response?.data?.detail || "No se pudo cargar el resumen")).finally(() => setLoading(false));
  }, [open, linea?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = pv ? (f.incluir_montaje && pv.montaje ? pv.total_con_montaje : pv.total_sin_montaje) : 0;
  const puede = pv?.puede_cerrar && f.motivo && (!f.incluir_montaje || f.justificacion_montaje.trim().length >= 5) && total > 0;

  const confirmar = async () => {
    setEnviando(true);
    try {
      const body = { motivo: f.motivo, incluir_montaje: f.incluir_montaje, justificacion_montaje: f.justificacion_montaje || null, observacion: f.observacion || null, nuevo: f.nuevo, total_confirmado: total };
      if (pv.es_envio && (f.transportista || f.tracking || f.fecha_salida)) body.envio = { transportista: f.transportista || null, tracking: f.tracking || null, fecha_salida: f.fecha_salida || null };
      const { data } = await api.post(`/lineas/${linea.id}/finalizar-sin-montaje`, body);
      setRes(data);
      if (data.fd_error) toast.warning(`Línea cerrada y albarán ${data.albaran_local} creado, pero FD falló: ${data.fd_error}`, { duration: 10000 });
      else toast.success(data.mensaje || `Línea cerrada · albarán ${data.albaran_local}`);
      onDone?.(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.msg || d || "No se pudo finalizar", { duration: 9000 });
      if (d?.code === "total_cambiado") api.get(`/lineas/${linea.id}/cierre-sin-montaje/preview`).then(({ data }) => setPv(data)).catch(() => {});
    } finally { setEnviando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="finalizar-sin-montaje-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PackageCheck className="w-4 h-4 text-amber-300" /> Finalizar sin montaje</DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Cierra la línea administrativamente y añade sus conceptos facturables al albarán del mes en FacturaDirecta (mismo flujo que un reporte). No crea ruta ni reporte.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-amber-400" /></div>}

        {pv && !res && (
          <div className="space-y-4 text-sm">
            <div className="text-xs text-zinc-400" data-testid="fsm-cabecera">
              <b className="text-zinc-200">{pv.peticion?.codigo}</b> · {pv.peticion?.nombre} · {pv.cliente?.nombre || <span className="text-rose-300">sin cliente</span>}
              {pv.espacio?.nombre && <> · 📍 {pv.espacio.nombre}</>} · estado línea <span className="font-mono">{pv.linea?.estado}</span>
            </div>

            {pv.bloqueos?.length > 0 && (
              <div className="bg-rose-500/10 border border-rose-500/40 rounded-lg p-3 space-y-1" data-testid="fsm-bloqueos">
                {pv.bloqueos.map((b) => <div key={b.code} className="flex items-start gap-2 text-xs text-rose-200" data-testid={`fsm-bloqueo-${b.code}`}><Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />{b.msg}</div>)}
              </div>
            )}

            <div>
              <Label className="text-[10px] text-zinc-500">Motivo del cierre</Label>
              <Select value={f.motivo} onValueChange={(v) => setF((p) => ({ ...p, motivo: v }))}>
                <SelectTrigger data-testid="fsm-motivo" className="bg-zinc-900 border-zinc-800 h-9"><SelectValue placeholder="Selecciona un motivo…" /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {pv.motivos.map((m) => <SelectItem key={m.key} value={m.key} data-testid={`fsm-motivo-${m.key}`}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3" data-testid="fsm-conceptos">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold mb-1">Producción / componentes facturables</div>
              {pv.produccion.length === 0 && <div className="text-xs text-zinc-500 italic">Sin componentes facturables</div>}
              <ul className="divide-y divide-zinc-800/70">{pv.produccion.map((it) => <Concepto key={it.componente_id} it={it} testId={`fsm-item-${it.componente_id}`} />)}</ul>
              {pv.componentes_omitidos?.length > 0 && (
                <div className="text-[10px] text-zinc-500 mt-1" data-testid="fsm-omitidos">No se facturan: {pv.componentes_omitidos.map((o) => `${o.nombre || o.componente_id?.slice(0, 6)} (${o.motivo})`).join(" · ")}</div>
              )}
              <div className="flex justify-between text-xs mt-2 pt-2 border-t border-zinc-800"><span className="text-zinc-400">Subtotal producción</span><span className="font-mono font-semibold" data-testid="fsm-subtotal-produccion">{eur(pv.subtotal_produccion)}</span></div>
            </div>

            <div className={`border rounded-lg p-3 ${f.incluir_montaje ? "bg-amber-500/10 border-amber-500/40" : "bg-zinc-900/40 border-zinc-800"}`} data-testid="fsm-montaje">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Montaje (separado)</div>
                  <div className="text-zinc-300">Precio de montaje de la línea: <b className="font-mono" data-testid="fsm-precio-montaje">{eur(pv.subtotal_montaje)}</b>{!pv.montaje && <span className="text-zinc-500"> · sin importe</span>}</div>
                  <div className="text-[10px] text-zinc-500">Por defecto NO se factura en un cierre sin montaje.</div>
                </div>
                <label className={`inline-flex items-center gap-1.5 text-xs cursor-pointer ${!pv.montaje ? "opacity-40" : ""}`}>
                  <input type="checkbox" disabled={!pv.montaje} checked={f.incluir_montaje} onChange={(e) => setF((p) => ({ ...p, incluir_montaje: e.target.checked }))} data-testid="fsm-incluir-montaje" /> Incluir montaje
                </label>
              </div>
              {f.incluir_montaje && (
                <Input value={f.justificacion_montaje} onChange={(e) => setF((p) => ({ ...p, justificacion_montaje: e.target.value }))} placeholder="Justificación obligatoria (¿por qué se factura montaje sin montar?)" data-testid="fsm-justificacion" className="mt-2 h-8 bg-zinc-950 border-amber-500/40 text-xs" />
              )}
            </div>

            {pv.es_envio && (
              <div className="grid grid-cols-3 gap-2" data-testid="fsm-envio">
                <div><Label className="text-[10px] text-zinc-500">Transportista</Label><Input value={f.transportista} onChange={(e) => setF((p) => ({ ...p, transportista: e.target.value }))} data-testid="fsm-transportista" className="h-8 bg-zinc-900 border-zinc-800 text-xs" /></div>
                <div><Label className="text-[10px] text-zinc-500">Nº seguimiento</Label><Input value={f.tracking} onChange={(e) => setF((p) => ({ ...p, tracking: e.target.value }))} data-testid="fsm-tracking" className="h-8 bg-zinc-900 border-zinc-800 text-xs" /></div>
                <div><Label className="text-[10px] text-zinc-500">Fecha de salida</Label><Input type="date" value={f.fecha_salida} onChange={(e) => setF((p) => ({ ...p, fecha_salida: e.target.value }))} data-testid="fsm-fecha-salida" className="h-8 bg-zinc-900 border-zinc-800 text-xs" /></div>
              </div>
            )}

            <div>
              <Label className="text-[10px] text-zinc-500">Observación (opcional)</Label>
              <Textarea value={f.observacion} onChange={(e) => setF((p) => ({ ...p, observacion: e.target.value }))} rows={2} data-testid="fsm-observacion" className="bg-zinc-900 border-zinc-800 text-xs" />
            </div>

            <div className="flex items-center justify-between bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2">
              <div className="text-xs text-zinc-400">Total previsto a albarán (sin IVA)<div className="text-[10px] text-zinc-500">{pv.produccion.length + (f.incluir_montaje && pv.montaje ? 1 : 0)} concepto(s)</div></div>
              <div className="text-xl font-bold font-mono text-amber-300" data-testid="fsm-total">{eur(total)}</div>
            </div>
            <label className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={f.nuevo} onChange={(e) => setF((p) => ({ ...p, nuevo: e.target.checked }))} data-testid="fsm-nuevo" /> Crear albarán FD nuevo (en vez de añadir al albarán abierto del mes)
            </label>
          </div>
        )}

        {res && (
          <div className="space-y-2 text-sm" data-testid="fsm-resultado">
            <div className="flex items-start gap-2 text-emerald-300"><CheckCircle2 className="w-5 h-5 shrink-0" /> Línea cerrada sin montaje · albarán local <b>{res.albaran_local}</b> · {eur(res.subtotal)} ({res.n_conceptos} concepto(s))</div>
            {res.fd ? <div className="text-xs text-zinc-300">FacturaDirecta: {res.fd.mensaje || res.fd.fd_number}</div>
              : <div className="flex items-start gap-2 text-xs text-amber-300"><AlertTriangle className="w-4 h-4 shrink-0" /> Envío a FD pendiente: {res.fd_error}. El albarán local existe; reintenta desde Albaranes.</div>}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={enviando}>{res ? "Cerrar" : "Cancelar"}</Button>
          {!res && pv && (
            <Button onClick={confirmar} disabled={!puede || enviando} data-testid="fsm-confirmar" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
              {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Cerrar y {f.nuevo ? "crear albarán en" : "añadir al albarán de"} FacturaDirecta
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
